import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Send, Quote, Star } from "lucide-react";
import { useTestimonials, useSubmitTestimonial } from "@/hooks/useTestimonials";
import { toast } from "sonner";

const StarRating = ({ value, onChange, readonly = false, size = 5 }: { value: number; onChange?: (v: number) => void; readonly?: boolean; size?: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-${size} w-${size} transition-colors ${
          i < value ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"
        } ${!readonly ? "cursor-pointer hover:scale-110" : ""}`}
        onClick={() => !readonly && onChange?.(i + 1)}
      />
    ))}
  </div>
);

const TestimonialsSection = () => {
  const { data: testimonials } = useTestimonials();
  const submitMutation = useSubmitTestimonial();
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(5);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Preencha seu nome e comentário.");
      return;
    }
    if (name.length > 60 || role.length > 60 || message.length > 500) {
      toast.error("Texto muito longo.");
      return;
    }
    try {
      await submitMutation.mutateAsync({
        name: name.trim(),
        role: role.trim(),
        message: message.trim(),
        rating,
      });
      toast.success("Obrigado! Seu comentário será exibido após aprovação.");
      setName("");
      setRole("");
      setMessage("");
      setRating(5);
    } catch {
      toast.error("Erro ao enviar. Tente novamente.");
    }
  };

  const approved = testimonials?.filter((t) => t.approved) || [];

  return (
    <section className="py-16 px-4 bg-secondary">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: "var(--font-serif)" }}>
            💛 O que músicos estão dizendo
          </h2>
        </motion.div>

        {approved.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {approved.slice(0, 6).map((t, i) => (
              <motion.div
                key={t.id}
                className="bg-card rounded-2xl p-6 shadow-sm border border-border flex flex-col justify-between"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Quote className="h-8 w-8 text-amber-500/40" />
                    <StarRating value={t.rating || 5} readonly size={4} />
                  </div>
                  <p className="text-foreground/80 text-base leading-relaxed italic" style={{ fontFamily: "var(--font-serif)" }}>
                    "{t.message}"
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border">
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {t.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm" style={{ fontFamily: "var(--font-serif)" }}>{t.name}</p>
                    {t.role && <p className="text-muted-foreground text-xs">{t.role}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          className="max-w-xl mx-auto bg-card rounded-2xl p-8 shadow-md border border-border"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-5">
            <MessageCircle className="h-5 w-5 text-amber-500" />
            <h3 className="font-bold text-foreground text-lg" style={{ fontFamily: "var(--font-serif)" }}>
              Deixe seu comentário
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Seu nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition-all text-sm"
                required
              />
              <input
                type="text"
                placeholder="Ex: Músico de igreja"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                maxLength={60}
                className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition-all text-sm"
              />
            </div>

            {/* Star Rating */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Sua avaliação:</span>
              <StarRating value={rating} onChange={setRating} />
            </div>

            <textarea
              placeholder="O que você está achando do site?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition-all text-sm resize-none"
              required
            />
            <button
              type="submit"
              disabled={submitMutation.isPending}
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-xl transition-all duration-300 text-sm disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {submitMutation.isPending ? "Enviando..." : "Enviar Comentário"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
